import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useLocation, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import { HttpError } from '@voll-intelligence/domain';

import { env } from '@web/core/env';
import { viewAsRepository, type ViewAsHit } from '@web/data/repositories';
import { t } from '@web/i18n';
import { Button } from '@web/ui/core/atoms';
import { useOrgContext } from '@web/ui/core/hooks';
import { EmptyHint } from '@web/ui/core/molecules';
import { SegmentedButton } from '@web/ui/core/organisms';

import { JustificationDialog } from '../components/justification_dialog';
import { AppFrame } from '../components/app_frame';
import { TravelerSearch } from '../components/traveler_search';
import {
  useCountdown,
  useReleaseOnLeave,
  useViewAsHandshake,
  type FramedSession,
} from '../hooks/use_view_as_session';
import { useFrameLayout } from '../hooks/use_frame_layout';

interface Target {
  customerId: string;
  travelerName: string;
  cardId?: string;
}

type Stage =
  | { kind: 'search' }
  | { kind: 'dialog'; target: Target; initial: string }
  | { kind: 'viewing'; target: Target; justification: string; session: FramedSession }
  | { kind: 'expired'; target: Target; justification: string };

const EntrySearchSchema = z.object({
  customerId: z.string().min(1),
  cardId: z.string().min(1).optional(),
  cardSeq: z.string().regex(/^\d+$/).optional(),
});
const EntryStateSchema = z.object({ travelerName: z.string().min(1) });

function cardSuggestion(seq: number | string | undefined): string {
  return seq === undefined ? '' : t('viewAs.dialog.cardSuggestion', { seq });
}

/**
 * Coming from a card (Task 16), the URL carries opaque ids and the history
 * state the traveler's name — never PII in the URL. Without the state (a
 * reload, a pasted link) the ids alone can't name the traveler: show search.
 */
function entryStage(search: unknown, state: unknown): Stage {
  const ids = EntrySearchSchema.safeParse(search);
  const named = EntryStateSchema.safeParse(state);
  if (!ids.success || !named.success) return { kind: 'search' };
  return {
    kind: 'dialog',
    target: {
      customerId: ids.data.customerId,
      travelerName: named.data.travelerName,
      cardId: ids.data.cardId,
    },
    initial: cardSuggestion(ids.data.cardSeq),
  };
}

function targetOf(hit: ViewAsHit): Target {
  return { customerId: hit.id, travelerName: hit.name, cardId: hit.openCards[0]?.id };
}

function originOf(url: string | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

/**
 * `/orgs/$orgSlug/view-as` — "Ver como viajante": the operator picks a
 * traveler, justifies the access, and sees the traveler's app (web-chat) in a
 * phone frame, read-only, until the grant expires or they close it.
 */
export function ViewAsPage(): React.JSX.Element {
  const webChatUrl = env.VITE_WEB_CHAT_URL;
  const webChatOrigin = originOf(webChatUrl);
  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      {webChatUrl && webChatOrigin ? (
        <ViewAs webChatUrl={webChatUrl} webChatOrigin={webChatOrigin} />
      ) : (
        <>
          <ViewAsTopBar />
          <div className="p-6">
            <EmptyHint>{t('viewAs.notConfigured')}</EmptyHint>
          </div>
        </>
      )}
    </div>
  );
}

function ViewAsTopBar({ children }: Readonly<{ children?: React.ReactNode }>): React.JSX.Element {
  return (
    <header className="flex items-center gap-3 border-b border-border-soft bg-surface px-5 py-3">
      <h1 className="flex-1 text-base font-medium text-foreground">{t('viewAs.title')}</h1>
      {children}
    </header>
  );
}

function openErrorCopy(error: unknown): string | null {
  if (!error) return null;
  return error instanceof HttpError && error.status === 409
    ? t('viewAs.dialog.notFound')
    : t('error.unexpected');
}

function ViewAs({
  webChatUrl,
  webChatOrigin,
}: Readonly<{ webChatUrl: string; webChatOrigin: string }>): React.JSX.Element {
  const { orgId } = useOrgContext();
  const search: unknown = useSearch({ strict: false });
  const location = useLocation();
  const [stage, setStage] = useState<Stage>(() => entryStage(search, location.state));
  // Bumped by every confirm, cancel and new dialog: a grant that answers for a
  // superseded attempt (the operator cancelled or moved on) is never shown.
  const attemptRef = useRef(0);
  // Leaving the page makes any open request in flight stale: TanStack still runs the
  // mutation's onSuccess after unmount, and the stale branch below releases the grant.
  useEffect(
    () => () => {
      attemptRef.current += 1;
    },
    [],
  );

  const close = useMutation({
    mutationFn: (grantId: string) => viewAsRepository.close(orgId, grantId),
  });
  const open = useMutation({
    mutationFn: (input: { target: Target; justification: string; attempt: number }) =>
      viewAsRepository.open(orgId, {
        customerId: input.target.customerId,
        justification: input.justification,
        cardId: input.target.cardId,
      }),
    // The dialog renders the failure itself (409 = traveler not found).
    meta: { silent: true },
    // The token lives only in the page state: drop the mutation (and its
    // result) from the client cache as soon as the stage holds it.
    gcTime: 0,
    onSuccess: (session, { target, justification, attempt }) => {
      if (attempt !== attemptRef.current) {
        // Aborted while in flight: release the grant, drop the token unseen.
        close.mutate(session.grantId);
        return;
      }
      open.reset();
      setStage({
        kind: 'viewing',
        target,
        justification,
        session: { ...session, travelerName: target.travelerName },
      });
    },
  });

  function supersedeAttempt(): void {
    attemptRef.current += 1;
    open.reset();
  }

  function openDialog(target: Target, initial: string): void {
    supersedeAttempt();
    setStage({ kind: 'dialog', target, initial });
  }

  if (stage.kind === 'viewing') {
    return (
      <ViewingPanel
        orgId={orgId}
        session={stage.session}
        webChatUrl={webChatUrl}
        webChatOrigin={webChatOrigin}
        onClose={() => {
          close.mutate(stage.session.grantId);
          setStage({ kind: 'search' });
        }}
        onExpired={() => {
          setStage({ kind: 'expired', target: stage.target, justification: stage.justification });
        }}
      />
    );
  }
  return (
    <>
      <ViewAsTopBar />
      <main className="flex-1 overflow-y-auto p-6">
        {stage.kind === 'expired' ? (
          <div className="mx-auto flex max-w-form-md flex-col items-center gap-3">
            <EmptyHint>{t('viewAs.expired')}</EmptyHint>
            <Button
              variant="primary"
              onClick={() => {
                openDialog(stage.target, stage.justification);
              }}
            >
              {t('viewAs.reopen')}
            </Button>
          </div>
        ) : (
          <TravelerSearch
            orgId={orgId}
            onPick={(hit) => {
              openDialog(targetOf(hit), cardSuggestion(hit.openCards[0]?.seq));
            }}
          />
        )}
      </main>
      {stage.kind === 'dialog' ? (
        <JustificationDialog
          travelerName={stage.target.travelerName}
          initial={stage.initial}
          pending={open.isPending}
          error={openErrorCopy(open.error)}
          onConfirm={(justification) => {
            attemptRef.current += 1;
            open.mutate({ target: stage.target, justification, attempt: attemptRef.current });
          }}
          onCancel={() => {
            supersedeAttempt();
            setStage({ kind: 'search' });
          }}
        />
      ) : null}
    </>
  );
}

function ViewingPanel({
  orgId,
  session,
  webChatUrl,
  webChatOrigin,
  onClose,
  onExpired,
}: Readonly<{
  orgId: string;
  session: FramedSession;
  webChatUrl: string;
  webChatOrigin: string;
  onClose: () => void;
  onExpired: () => void;
}>): React.JSX.Element {
  const frameRef = useViewAsHandshake(session, webChatOrigin);
  const settledRef = useReleaseOnLeave(orgId, session.grantId);
  const remainingMs = useCountdown(session.expiresAt);
  const [layout, setLayout] = useFrameLayout();
  const expired = remainingMs === 0;
  useEffect(() => {
    if (!expired) return;
    settledRef.current = true;
    onExpired();
  }, [expired, onExpired, settledRef]);
  return (
    <>
      <ViewAsTopBar>
        <span className="text-meta text-muted-foreground">
          {t('viewAs.header', {
            name: session.travelerName,
            minutes: Math.ceil(remainingMs / 60_000),
          })}
        </span>
        <SegmentedButton ariaLabel={t('viewAs.frame.layout')}>
          <SegmentedButton.Item
            label={t('viewAs.frame.phone')}
            isActive={layout === 'phone'}
            onClick={() => {
              setLayout('phone');
            }}
          />
          <SegmentedButton.Item
            label={t('viewAs.frame.desktop')}
            isActive={layout === 'desktop'}
            onClick={() => {
              setLayout('desktop');
            }}
          />
        </SegmentedButton>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            settledRef.current = true;
            onClose();
          }}
        >
          {t('viewAs.close')}
        </Button>
      </ViewAsTopBar>
      {session.travelerOnApp === false ? (
        <p className="border-b border-border-soft bg-tone-amber-tint px-5 py-2 text-meta text-tone-amber-ink">
          {t('viewAs.notOnAppHint')}
        </p>
      ) : null}
      <main className="flex flex-1 overflow-auto p-6">
        <AppFrame
          src={webChatUrl}
          title={t('viewAs.frame.title', { name: session.travelerName })}
          frameRef={frameRef}
          layout={layout}
        />
      </main>
    </>
  );
}
